import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, inject, provideZoneChangeDetection, provideAppInitializer } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { provideFastSVG } from '@push-based/ngx-fast-svg';
import { INSIDE_APP_ENVIRONMENT } from '@wheere-front/inside-shared-util';
import { provideReplaySiteBuilding, SITEMANAGER_BASE_PATH } from '@wheere-front/domain-sites';
import { TRACKERMANAGER_BASE_PATH } from '@wheere-front/domain-trackers';
import { GATEWAYMANAGER_BASE_PATH } from '@wheere-front/domain-gateways';
import { MAPTILES_KEY } from '@wheere-front/shared-map-libre';
import { provideMaplibreWorker } from '@wheere-front/shared-map-libre/worker';
import { USERMANAGER_BASE_PATH } from '@wheere-front/domain-users';
import { authInterceptor, provideKeycloak } from '@wheere-front/shared-auth';
import { ADMIN_SCOPE_STORAGE_KEY, LanguageService, provideTooltip, ThemeService } from '@wheere-front/shared-ui';
import { APP_ENVIRONMENT_KEYCLOAK } from '@wheere-front/shared-util';
import { BASE_PATH as OPENAPI_GATEWAY } from '@wheere/api-gateway';
import { BASE_PATH as OPENAPI_SITE } from '@wheere/api-site';
import { BASE_PATH as OPENAPI_TRACKER } from '@wheere/api-tracker';
import { BASE_PATH as OPENAPI_USER } from '@wheere/api-user';
import { BASE_PATH as OPENAPI_NOTIFICATION } from '@wheere/api-notification';
import { KeycloakOptions } from 'keycloak-angular';
import { environment } from '../environments';
import { routes } from './app.routes';
import { provideInsideCommandPalette } from './command-palette.config';
import { configTransloco } from './core/transloco-config';
import { providePrimeNG } from 'primeng/config';
import { PrimengPresetConfig } from '@wheere-front/shared-primeng-preset';
import { MessageService } from 'primeng/api';
import { importProvidersFrom } from '@angular/core';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';

const keycloakOptions: KeycloakOptions = {
  config: {
    url: environment.keycloak.issuer,
    realm: environment.keycloak.realm,
    clientId: environment.keycloak.clientId,
  },
  loadUserProfileAtStartUp: true,
  initOptions: {
    onLoad: 'login-required',
    checkLoginIframe: false,
    pkceMethod: 'S256',
  },
  bearerExcludedUrls: ['/assets'],
  enableBearerInterceptor: true,
};

export const libConfig: ApplicationConfig = {
  providers: [
    // Bâtiment 3D du site sur la carte de rejeu des positions : inside seulement (ADR-017).
    provideReplaySiteBuilding(),
    { provide: OPENAPI_TRACKER, useValue: environment.api.tracker },
    { provide: OPENAPI_GATEWAY, useValue: environment.api.gateway },
    { provide: OPENAPI_SITE, useValue: environment.api.site },
    // domain-trackers TrackersV7Store (used by <lib-tracker-graphs> on the tracker Telemetry tab).
    { provide: TRACKERMANAGER_BASE_PATH, useValue: environment.api.tracker },
    // domain-gateways GatewaysV7Store (used by <lib-gateway-graphs> on the gateway Telemetry tab).
    { provide: GATEWAYMANAGER_BASE_PATH, useValue: environment.api.gateway },
    // MapStateService (used by <lib-position-history-table> "show on map") needs the maptiles key.
    { provide: MAPTILES_KEY, useValue: environment.maptiles ?? '' },
    provideMaplibreWorker(),
    // Namespaced per app: inside and the integrator each keep their own picked org scope when
    // both run in the same browser.
    { provide: ADMIN_SCOPE_STORAGE_KEY, useValue: 'wheere.inside.adminScope.orgId.v1' },
    // Palette ⌘K : types permis par les gardes de route (baromètres compris).
    provideInsideCommandPalette(),
    // V8: api-user generated client (used by OrganizationFilterComponent in domain-users).
    // Without this, the service falls back to its OpenAPI-spec default basePath
    // `https://usermanager` and the request fails with `https://usermanager/organizations`.
    { provide: OPENAPI_USER, useValue: environment.api.user },
    // Hub de notifications (flux 18) : cloche, historique, réglages.
    { provide: OPENAPI_NOTIFICATION, useValue: environment.api.notification },
    // V7 + V5a: domain-* libs use these tokens. Reuses the same hostnames as the OpenAPI clients.
    { provide: SITEMANAGER_BASE_PATH, useValue: environment.api.site },
    { provide: USERMANAGER_BASE_PATH, useValue: environment.api.user },
    { provide: INSIDE_APP_ENVIRONMENT, useValue: environment },
    { provide: APP_ENVIRONMENT_KEYCLOAK, useValue: environment.keycloak },
    { provide: MessageService },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideRouter(
      routes,
      withComponentInputBinding(),
      // V5b: child tab routes (members/audit/settings) need `:orgId` from the parent route to be
      // injected into their `input.required<string>('orgId')` — only happens with 'always'.
      withRouterConfig({ paramsInheritanceStrategy: 'always' })
    ),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: PrimengPresetConfig,
        options: {
          // Align PrimeNG Aura dark mode with the `.dark` class the shared ThemeService
          // toggles on <html> (and that Tailwind darkMode:'class' keys off).
          darkModeSelector: '.dark',
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, tailwind-utilities,primeng',
          },
        },
      },
    }),
    provideKeycloak(keycloakOptions),
    provideTransloco(configTransloco),
    // Load the active language's JSON before first paint (mirrors the integrator) so transloco
    // pipes render the right strings on first render instead of raw keys. Touching LanguageService
    // triggers its constructor side-effects (setActiveLang + dayjs.locale).
    provideAppInitializer(() => {
      const initializerFn = (() => {
        const lang = inject(LanguageService);
        const transloco = inject(TranslocoService);
        // Instantiate the shared ThemeService at boot so it applies the resolved theme (system /
        // light / dark) on <html> before first paint. The whole inside chrome + pages now carry
        // dark: variants, so the default follows the OS preference — same as the integrator.
        inject(ThemeService);
        return async () => {
          const active = lang.active();
          try {
            await firstValueFrom(transloco.load(active));
          } catch {
            // Loader failure — transloco retries lazily.
          }
        };
      })();
      return initializerFn();
    }),
    provideTooltip(),
    provideFastSVG({
      url: (name: string) => `assets/svg/${name}.svg`,
    }),
    importProvidersFrom(
      MonacoEditorModule.forRoot({
        baseUrl: 'assets/monaco/vs',
        defaultOptions: { scrollBeyondLastLine: false },
      })
    ),
  ],
};
